<template>
  <div class="mcp-admin-panel">
    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = ''">
      {{ errorMessage }}
    </b-alert>

    <!-- Switched off by the backend in production (dent-cloud-service MCP_ADMIN_DISABLED_ENVS) -->
    <b-alert v-if="!isLoading && overview.enabled === false" show variant="warning">
      <i class="fas fa-ban mr-1"></i>
      The admin MCP connector is not available in this environment ({{ overview.environment || "unknown" }}).
      Use it on staging or gamma.
    </b-alert>

    <!-- Connector URL -->
    <b-card v-if="overview.enabled !== false" class="mb-3">
      <template #header>
        <h5 class="mb-0">
          <i class="fas fa-plug mr-2"></i>
          Connector URL
        </h5>
      </template>
      <p class="text-muted mb-2">
        Add this URL as a custom connector in Claude (or another MCP client) and sign in with a superadmin
        account. It reads every clinic and branch, read-only; patient names are shown as initials only.
      </p>
      <b-input-group>
        <b-form-input :value="overview.connectorUrl" readonly class="text-monospace" />
        <b-input-group-append>
          <b-button variant="outline-primary" :disabled="!overview.connectorUrl" @click="copyUrl">
            <i class="fas fa-copy mr-1"></i>{{ copied ? "Copied" : "Copy" }}
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <small class="text-muted d-block mt-2">
        Access follows the superadmin email list. Revoke a connection below to cut it off at once.
      </small>
    </b-card>

    <!-- Connections -->
    <b-card class="mb-3" no-body>
      <template #header>
        <b-row class="align-items-center">
          <b-col>
            <h5 class="mb-0">
              <i class="fas fa-link mr-2"></i>
              Connections
              <b-badge v-if="activeCount > 0" variant="success" class="ml-2">{{ activeCount }} active</b-badge>
            </h5>
          </b-col>
          <b-col cols="auto">
            <b-button size="sm" variant="outline-secondary" :disabled="isLoading" @click="load">
              <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': isLoading }"></i>
              Refresh
            </b-button>
          </b-col>
        </b-row>
      </template>
      <b-table
        :items="overview.connections"
        :fields="connectionFields"
        :busy="isLoading"
        small
        responsive
        show-empty
        empty-text="No connections yet"
        class="mb-0"
      >
        <template #cell(user)="row">
          <div>{{ row.item.email || `uid ${row.item.uid}` }}</div>
          <small v-if="row.item.name" class="text-muted">{{ row.item.name }}</small>
        </template>
        <template #cell(client)="row">
          <div>{{ row.item.clientName || "-" }}</div>
          <small v-if="row.item.redirectHost" class="text-muted">{{ row.item.redirectHost }}</small>
        </template>
        <template #cell(createdAt)="row">{{ formatDateTime(row.item.createdAt) }}</template>
        <template #cell(lastUsedAt)="row">{{ formatDateTime(row.item.lastUsedAt) }}</template>
        <template #cell(status)="row">
          <b-badge v-if="row.item.active" variant="success">Active</b-badge>
          <template v-else>
            <b-badge variant="secondary">Revoked</b-badge>
            <small class="d-block text-muted">
              {{ formatDateTime(row.item.revokedAt) }}
              <span v-if="row.item.revokedByEmail">by {{ row.item.revokedByEmail }}</span>
            </small>
          </template>
        </template>
        <template #cell(actions)="row">
          <b-button
            v-if="row.item.active"
            size="sm"
            variant="outline-danger"
            :disabled="revokingId === row.item.id"
            @click="confirmRevoke(row.item)"
          >
            <b-spinner v-if="revokingId === row.item.id" small class="mr-1" />
            Revoke
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Recent tool calls -->
    <b-card no-body>
      <template #header>
        <h5 class="mb-0">
          <i class="fas fa-history mr-2"></i>
          Recent tool calls
          <small class="text-muted ml-2">latest {{ overview.recentCalls.length }}</small>
        </h5>
      </template>
      <b-table
        :items="overview.recentCalls"
        :fields="callFields"
        :busy="isLoading"
        small
        responsive
        show-empty
        empty-text="No calls yet"
        class="mb-0"
      >
        <template #cell(createdAt)="row">{{ formatDateTime(row.item.createdAt, true) }}</template>
        <template #cell(status)="row">
          <b-badge :variant="statusVariant(row.item.status)">{{ row.item.status }}</b-badge>
          <small v-if="row.item.errorCode" class="ml-1 text-muted">{{ row.item.errorCode }}</small>
        </template>
        <template #cell(rowCount)="row">{{ row.item.rowCount === null ? "-" : row.item.rowCount }}</template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import moment from "moment";

// Superadmin page for the cross-clinic MCP endpoint (BE .superpower/superadmin-mcp/01-design.md §2a).
export default {
  name: "McpAdminPanel",
  data() {
    return {
      overview: { enabled: null, environment: null, connectorUrl: "", connections: [], recentCalls: [] },
      isLoading: false,
      revokingId: null,
      copied: false,
      errorMessage: "",
      connectionFields: [
        { key: "user", label: "User" },
        { key: "client", label: "AI client" },
        { key: "createdAt", label: "Connected" },
        { key: "lastUsedAt", label: "Last used" },
        { key: "status", label: "Status" },
        { key: "actions", label: "", class: "text-right" },
      ],
      callFields: [
        { key: "createdAt", label: "Time" },
        { key: "email", label: "User" },
        { key: "tool", label: "Tool" },
        { key: "status", label: "Status" },
        { key: "rowCount", label: "Rows", class: "text-right" },
        { key: "latencyMs", label: "ms", class: "text-right" },
      ],
    };
  },
  computed: {
    activeCount() {
      return this.overview.connections.filter((c) => c.active).length;
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatDateTime(value, withSeconds = false) {
      return value ? moment(value).format(withSeconds ? "DD/MM/YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm") : "-";
    },
    statusVariant(status) {
      if (status === "OK") return "success";
      if (status === "DENIED") return "danger";
      return "warning";
    },
    async load() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const data = await this.$store.dispatch("moduleSuperadmin/fetchMcpAdmin");
        this.overview = {
          enabled: data.enabled !== false,
          environment: data.environment || null,
          connectorUrl: data.connectorUrl || "",
          connections: data.connections || [],
          recentCalls: data.recentCalls || [],
        };
      } catch (error) {
        this.errorMessage = "Could not load the admin MCP overview. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.overview.connectorUrl);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (error) {
        this.errorMessage = "Copy failed. Select the URL and copy it manually.";
      }
    },
    async confirmRevoke(connection) {
      const who = connection.email || `uid ${connection.uid}`;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Revoke the ${connection.clientName || "AI client"} connection of ${who}? Its next tool call is denied and the user must reconnect.`,
        {
          title: "Revoke admin MCP connection",
          okVariant: "danger",
          okTitle: "Revoke",
          cancelTitle: "Cancel",
          centered: true,
        }
      );
      if (!confirmed) return;
      this.revokingId = connection.id;
      let failed = false;
      try {
        await this.$store.dispatch("moduleSuperadmin/revokeMcpAdminConnection", connection.id);
      } catch (error) {
        failed = true;
      } finally {
        this.revokingId = null;
      }
      // Reload either way: a dropped response may still have revoked it server-side.
      await this.load();
      if (failed) this.errorMessage = "Revoke failed. Please try again.";
    },
  },
};
</script>
